import { PresetTag, SoftTag } from '../../Tag/SoftTag';
import { GREEN } from '../../Theme/palette';
import { CheckCircleOutlined, MinusCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { L } from '@lib';
import { PurchaseOrder, PurchaseOrderMatchResult, PurchaseOrderMatchStatus } from '@services';
import { InputNumber, Select, Tooltip, Typography } from 'antd';
import React from 'react';

const { Text } = Typography;

interface IProps {
  result: PurchaseOrderMatchResult;
  receivedQty: number;
  costPrice: number | undefined;
  selectedPOId: string | undefined;
  onReceivedQtyChange: (val: number) => void;
  onCostPriceChange: (val: number | undefined) => void;
  onSelectPO: (id: string) => void;
}

const statusBadge = (status: PurchaseOrderMatchStatus) => {
  if (status === 'Matched')
    return <SoftTag icon={<CheckCircleOutlined />} group="green" style={{ margin: 0 }}>{L('Matched')}</SoftTag>;
  if (status === 'MultipleCandidates')
    return <SoftTag icon={<QuestionCircleOutlined />} group="yellow" style={{ margin: 0 }}>{L('Multiple candidates')}</SoftTag>;
  return <PresetTag icon={<MinusCircleOutlined />} color="default" style={{ margin: 0 }}>{L('No match')}</PresetTag>;
};

export const POReceiveLineCard: React.FC<IProps> = ({
  result,
  receivedQty,
  costPrice,
  selectedPOId,
  onReceivedQtyChange,
  onCostPriceChange,
  onSelectPO,
}) => {
  const { parsedLine, matchStatus, candidates } = result;
  const isNoMatch = matchStatus === 'NoMatch';

  return (
    <div
      style={{
        padding: '10px 14px',
        borderRadius: 6,
        border: '1px solid #f0f0f0',
        marginBottom: 8,
        background: isNoMatch ? '#fafafa' : '#fff',
        opacity: isNoMatch ? 0.55 : 1,
      }}
    >
      {/* Parsed line info */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
        <div>
          <Text strong style={{ fontSize: 13 }}>{parsedLine.name}</Text>
          {parsedLine.partNumber && (
            <Text type="secondary" style={{ fontSize: 11, marginLeft: 8 }}>
              {L('Part #')} {parsedLine.partNumber}
            </Text>
          )}
          <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 2 }}>
            {parsedLine.quantity != null && <span>{L('Invoice qty')}: {parsedLine.quantity}</span>}
            {parsedLine.unitPrice != null && (
              <span style={{ marginLeft: 8 }}>{L('Unit price')}: £{parsedLine.unitPrice.toFixed(2)}</span>
            )}
          </div>
        </div>
        {statusBadge(matchStatus)}
      </div>

      {/* Candidate PO selector for multiple matches */}
      {matchStatus === 'MultipleCandidates' && (
        <div style={{ marginBottom: 8 }}>
          <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 4 }}>
            {L('Select purchase order')}
          </Text>
          <Select
            style={{ width: '100%' }}
            value={selectedPOId}
            onChange={onSelectPO}
            options={candidates.map((c: PurchaseOrder) => ({
              value: c.id,
              label: `${c.partName}${c.partNumber ? ` · ${c.partNumber}` : ''} (${L('Qty')} ${c.quantity})`,
            }))}
          />
        </div>
      )}

      {/* Matched PO summary */}
      {matchStatus === 'Matched' && result.bestMatch && (
        <div style={{ fontSize: 11, color: GREEN.badge, marginBottom: 8 }}>
          ✓ {L('Matched to PO')}: <strong>{result.bestMatch.partName}</strong>
          {result.bestMatch.partNumber && <span> · {result.bestMatch.partNumber}</span>}
          {` (${L('Ordered qty')}: ${result.bestMatch.quantity})`}
        </div>
      )}

      {/* Editable fields — only for matched/multi lines */}
      {!isNoMatch && (
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 2 }}>
              {L('Received qty')}
            </Text>
            <InputNumber
              min={0.01}
              value={receivedQty}
              onChange={(v) => v != null && onReceivedQtyChange(v)}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text style={{ fontSize: 11, color: '#8c8c8c', display: 'block', marginBottom: 2 }}>
              {L('Cost price (£)')}
            </Text>
            <Tooltip title={L('Leave blank to use existing cost price')}>
              <InputNumber
                min={0}
                precision={2}
                step={0.01}
                value={costPrice}
                onChange={(v) => onCostPriceChange(v ?? undefined)}
                style={{ width: '100%' }}
                placeholder={L('From invoice')}
              />
            </Tooltip>
          </div>
        </div>
      )}

      {isNoMatch && (
        <Text type="secondary" style={{ fontSize: 11 }}>
          {L('No matching open purchase order — will be skipped')}
        </Text>
      )}
    </div>
  );
};
